
import {Component, Prop, Watch} from "vue-property-decorator";
import {HcPageModel} from "@dev/hc-components";
import {EnumUtil} from "@/app";
import EnvType from "@/module/main/page/EnvTypeEnum";
import EnvPreprod from "@/module/main/page/EnvPreprodEnum";
import EnvProd from "@/module/main/page/EnvProdEnum";
import IEnvSettings from "@/module/main/model/applicationForm/IEnvSettings";
import Brand from "@/module/main/model/applicationForm/BrandEnum";
import TemplateOptions from "@/module/main/page/TemplateOptionsEnum";
import Brands from "@/module/main/model/Brands";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import FinancingApiVersion from "@/module/main/dto/FinancingApiVersionEnum";
import CarLoanType from "@/module/main/dto/CarLoanTypeEnum";

@Component({})
export default class EnvSettings extends HcPageModel {
    @Prop({ type: Object }) public value!: IEnvSettings;
    @Prop({ type: String }) public template!: TemplateOptions;

    protected pageName: string = "envSettings";

    @Watch("value.financedSubjectType")
    private watchFinancedSubjectType(
        newValue: FinancedSubjectType,
        oldValue: FinancedSubjectType
    ): void {
        if (!Brands.getBrands(newValue).includes(this.value.brand)) {
            this.setCredential(Brands.getBrand(newValue, this.value.orderType, this.value.carLoanType));
        }
    }
    @Watch("value.orderType")
    private watchOrderType(
        newValue: OrderType,
        oldValue: OrderType
    ): void {
        if (!Brands.getBrands(this.value.financedSubjectType, newValue, this.value.carLoanType).includes(this.value.brand)) {
            this.setCredential(Brands.getBrand(this.value.financedSubjectType, newValue, this.value.carLoanType));
        }
    }
    @Watch("value.carLoanType")
    private watchCarLoanType(
        newValue: CarLoanType,
        oldValue: CarLoanType
    ): void {
        if (!Brands.getBrands(this.value.financedSubjectType, this.value.orderType, newValue).includes(this.value.brand)) {
            this.setCredential(Brands.getBrand(this.value.financedSubjectType, this.value.orderType, newValue));
        }
    }

    private get isEmbedded(): object[] {
        return [
            {
                label: this.$localize("pg-main-setting-isembedded-radio-true"),
                value: "true"
            },
            {
                label: this.$localize("pg-main-setting-isembedded-radio-false"),
                value: "false"
            }
        ];
    };

    private get brands(): Brand[] {
        return Brands.getBrands(this.value.financedSubjectType, this.value.orderType, this.value.carLoanType);
    }

    private isWideLogo(brand: Brand): boolean {
        // Auto Louda logo is very wide (221 x 19), in the default square button it is unreadable
        return brand === Brand.AUTO_LOUDA;
    }

    private setCredential(brand: Brand): void {
        this.value.brand = brand;
        const envCredential = Brands.getCredential(brand, this.template);
        if (!!envCredential) {
            this.value.username = envCredential.username;
            this.value.password = envCredential.password;
        }
        this.$store.dispatch("main/setEnvBrand", brand);
    }

    private get envTypeData(): object[] {
        return EnumUtil.createSelectDataFromEnum(EnvType);
    }

    private get isEnvTypeTest(): boolean {
        return this.value.envType == EnvType.TEST;
    }

    private get isStandardOrder(): boolean {
        return this.value.financedSubjectType === FinancedSubjectType.ORDER && this.value.orderType === OrderType.STANDARD;
    }

    private get financingApiVersionData(): object[] {
        return EnumUtil.createSelectDataFromEnum(FinancingApiVersion, "FinancingApiVersion");
    }

    private get envTypeNonTestData(): object[] {
        let data: object[] = [];
        switch (this.value.envType) {
            case EnvType.PREPROD:
                data = EnumUtil.createSelectDataFromEnum(EnvPreprod);
                break;
            case EnvType.PROD:
                data = EnumUtil.createSelectDataFromEnum(EnvProd);
                break;
        }

        return data;
    }

    private onEnvTypeChange(envType: EnvType): void
    {
        switch (envType) {
            case EnvType.TEST:
                this.value.env = "";
                break;
            case EnvType.PREPROD:
                this.value.env = EnumUtil.getEnumKey(EnvPreprod,
                    this.template == TemplateOptions.CZ ? EnvPreprod.CZ01P : EnvPreprod.SK01P);
                break;
            case EnvType.PROD:
                this.value.env = EnumUtil.getEnumKey(EnvProd,
                    this.template == TemplateOptions.CZ ? EnvProd.CZ : EnvProd.SK);
                break;
        }

        this.saveAndEmitData();
    }

    private onEnvChange(env: string): void
    {
        this.saveAndEmitData();
    }

    private onEnvBlur(): void
    {
        this.value.env = this.value.env.toLowerCase();
        this.saveAndEmitData();
    }

    private onShowQrCodeChange(): void {
        this.saveAndEmitData();
    }

    private onCopyToClipboardChange(): void {
        this.saveAndEmitData();
    }

    private onFinancingApiVersionChange(financingApiVersion: FinancingApiVersion): void {
        this.$store.dispatch("main/setFinancingApiVersion", financingApiVersion);
        this.$emit("financingApiVersionChanged");
    }

    private saveAndEmitData(): void
    {
        this.$store.dispatch("main/setEnvType", this.value.envType);
        this.$store.dispatch("main/setEnv", this.value.env);
        this.$store.dispatch("main/setEnvBrand", this.value.brand);
        this.$store.dispatch("main/setShowQrCode", this.value.showQrCode);
        this.$store.dispatch("main/setCopyToClipboard", this.value.copyToClipboard);

        this.$emit("envSettingsChanged");
    }
}
